import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Monitor, Wrench, Globe, Settings, Smartphone, ChevronDown, User, LogOut, Ticket, CreditCard, FileText, Crown, Users, Database, Grid } from 'lucide-react';
import { FaAndroid } from 'react-icons/fa';
import { Link } from './ui/Link';
import { Modal } from './ui/Modal';
import LoginForm from './LoginForm';
import RegisterModal from './RegisterModal';
import { useAuth } from '../contexts/AuthContext';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { getImageUrl, DEFAULT_IMAGE } from '../utils/imageUtils';

// Types pour la configuration des onglets
type TabId = 'profile' | 'dashboard' | 'freelancers' | 'users' | 'tickets' | 'subscription' | 'invoices' | 'preferences' | 'ticket_database';

type TabConfig = {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  roles?: string[];
  excludeRoles?: string[];
};

// Configuration des onglets du dashboard
const tabConfigs: TabConfig[] = [
  {
    id: 'profile',
    label: 'Mon profil',
    icon: <User className="w-5 h-5" />,
  },
  {
    id: 'dashboard',
    label: 'Tableau de bord',
    icon: <Grid className="w-5 h-5" />,
    roles: ['fondateur', 'admin', 'freelancer_admin'],
  },
  {
    id: 'users',
    label: 'Les utilisateurs',
    icon: <Users className="w-5 h-5" />,
    roles: ['fondateur', 'admin', 'freelancer_admin'],
  },
  {
    id: 'freelancers',
    label: 'Les freelancers',
    icon: <Users className="w-5 h-5" />,
    roles: ['fondateur', 'admin', 'freelancer_admin'],
  },
  {
    id: 'tickets',
    label: 'Mes tickets',
    icon: <Ticket className="w-5 h-5" />,
    excludeRoles: ['admin', 'fondateur', 'freelancer', 'freelancer_admin'],
  },
  {
    id: 'ticket_database',
    label: 'Base de tickets',
    icon: <Database className="w-5 h-5" />,
    roles: ['fondateur', 'admin', 'freelancer', 'freelancer_admin'],
  },
  {
    id: 'subscription',
    label: 'Mon abonnement',
    icon: <CreditCard className="w-5 h-5" />,
    excludeRoles: ['admin', 'fondateur', 'freelancer', 'freelancer_admin'],
  },
  {
    id: 'invoices',
    label: 'Mes factures',
    icon: <FileText className="w-5 h-5" />,
    excludeRoles: ['admin', 'fondateur', 'freelancer', 'freelancer_admin'],
  },
  {
    id: 'preferences',
    label: 'Préférences',
    icon: <Settings className="w-5 h-5" />,
  }
];

// Fonction helper pour vérifier si un utilisateur a accès à un onglet
const canAccessTab = (tab: TabConfig, userRole?: string): boolean => {
  if (!userRole) return !tab.roles && !tab.excludeRoles;
  
  if (tab.roles && tab.roles.length > 0) {
    return tab.roles.includes(userRole);
  }
  
  if (tab.excludeRoles && tab.excludeRoles.length > 0) {
    return !tab.excludeRoles.includes(userRole);
  }
  
  return true;
};

type SubscriptionType = "none" | "solo" | "family";

type NavDropdownItem = { name: string; href: string; icon: React.ReactNode };

type NavService = { name: string; href: string; icon: React.ReactNode; dropdown: NavDropdownItem[] };

const Navbar: React.FC = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileDropdowns, setMobileDropdowns] = useState<Record<number, boolean>>({});
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType>("none");
  const [profileImageError, setProfileImageError] = useState(false);
  


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Même hauteur réservée (pt-24 / md:pt-32) + état scroll cohérent au changement de page
  useEffect(() => {
    setIsScrolled(window.scrollY > 10);
    setIsOpen(false);
    setMobileDropdowns({});
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuRef]);

  useEffect(() => {
    if (user && user.subscriptionType) {
      setSubscriptionType(user.subscriptionType as SubscriptionType);
    } else {
      setSubscriptionType("none");
    }
  }, [user]);

  // Réinitialiser l'erreur d'image quand la photo de profil change
  useEffect(() => {
    setProfileImageError(false);
  }, [user?.profileImage]);

  const services: NavService[] = [
    {
      name: "Réparation téléphone",
      href: "/reparation-telephone",
      icon: <Smartphone className="h-5 w-5" />,
      dropdown: [
        {
          name: "iPhone",
          href: "/reparation-telephone#devis",
          icon: <Smartphone className="h-5 w-5" />
        },
        {
          name: "Android",
          href: "/reparation-telephone#devis",
          icon: <FaAndroid className="h-5 w-5" />
        }
      ]
    },
    {
      name: "Dépannage informatique",
      href: "/depannage-informatique",
      icon: <Wrench className="h-5 w-5" />,
      dropdown: [
        {
          name: "Particuliers",
          href: "/depannage-informatique#particuliers",
          icon: <User className="h-5 w-5" />
        },
        {
          name: "TPE/PME",
          href: "/depannage-informatique#tpe-pme",
          icon: <Users className="h-5 w-5" />
        }
      ]
    },
    {
      name: "Création site web",
      href: "/creation-site-web",
      icon: <Globe className="h-5 w-5" />,
      dropdown: [
        {
          name: "Offre site pro",
          href: "/creation-site-web#web-vitrine",
          icon: <Monitor className="h-5 w-5" />
        },
        {
          name: "E-commerce sur devis",
          href: "/creation-site-web#web-ecommerce",
          icon: <CreditCard className="h-5 w-5" />
        }
      ]
    }
  ];

  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
  };

  const handleUserButtonClick = () => {
    if (isAuthenticated) {
      setIsUserMenuOpen(!isUserMenuOpen);
    } else {
      setIsLoginModalOpen(true);
    }
  };

  const openRegisterModal = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(true);
  };

  const openLoginModal = () => {
    setIsRegisterModalOpen(false);
    setIsLoginModalOpen(true);
  };

  const getSubscriptionName = () => {
    switch(subscriptionType) {
      case "solo": return "Plan Solo";
      case "family": return "Plan Famille";
      case "none": 
      default: return "A la carte";
    }
  };

  const toggleMobileDropdown = (index: number) => {
    setMobileDropdowns(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[#36393F] shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24 md:h-32">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <RouterLink to="/" className="flex items-center">
                  <img
                    src="/images/brand/logo-image.webp" width={171} height={256} fetchPriority="high"
                    alt="SOS IT Services – réparation iPhone et informatique à Viry"
                    className="h-24 md:h-32 w-auto max-w-[360px] md:max-w-[480px] object-contain bg-transparent"
                  />
                </RouterLink>
              </div>
            </div>
            
            <div className="hidden md:block">
              <div className="ml-10 flex items-center space-x-4">
                {services.map((service, index) => (
                  <div key={index} className="relative group">
                    <div>
                      <button
                        type="button"
                        className="flex items-center text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors group-hover:text-white"
                      >
                        {service.icon}
                        <span className="ml-2">{service.name}</span>
                        <ChevronDown className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
                      </button>
                      <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-[#2F3136] ring-1 ring-black/5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-left">
                        <div className="py-1">
                          {service.dropdown.map((item, dropdownIndex) => (
                            <Link
                              key={dropdownIndex}
                              href={item.href}
                              className="flex items-center px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-[#5865F2] transition-colors"
                            >
                              {item.icon}
                              <span className="ml-2">{item.name}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <RouterLink
                  to="/contact"
                  className="bg-[#5865F2] hover:bg-opacity-90 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center"
                >
                  Nous contacter
                </RouterLink>
                <div className="relative" ref={userMenuRef}>
                  <button 
                    className="flex items-center justify-center p-1 rounded-full hover:bg-[#36393F] transition-colors"
                    aria-label={isAuthenticated ? 'Menu utilisateur' : 'Se connecter'}
                    onClick={handleUserButtonClick}
                  >
                    {isAuthenticated ? (
                      (() => {
                        const avatarUrl = user?.profileImage ? getImageUrl(user.profileImage) : null;
                        const hasRealPhoto = avatarUrl && avatarUrl !== DEFAULT_IMAGE && !profileImageError;
                        return hasRealPhoto ? (
                          <img
                            src={avatarUrl}
                            alt={`${user!.firstName} ${user!.lastName}`}
                            className="h-8 w-8 rounded-full object-cover"
                            onError={() => setProfileImageError(true)}
                            key={user!.profileImage}
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-[#5865F2] flex items-center justify-center">
                            <User className="h-5 w-5 text-white" />
                          </div>
                        );
                      })()
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-[#4E5058] flex items-center justify-center">
                        <User className="h-5 w-5 text-white" />
                      </div>
                    )}
                  </button>
                  
                  {/* Menu utilisateur */}
                  {isAuthenticated && (
                    <div 
                      className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-[#2F3136] ring-1 ring-black/5 transition-all duration-200 ${isUserMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                      style={{ minWidth: 'max-content', maxWidth: '90vw', overflowX: 'auto' }}
                    >
                      <div className="py-1">
                        <div className="px-4 py-2 text-sm text-gray-400 border-b border-gray-700">
                          Connecté en tant que<br />
                          <div className="flex flex-col mt-1">
                            <span className="font-semibold text-white mb-1">{user?.firstName} {user?.lastName}</span>
                            <span className="text-xs text-gray-400">{user?.email}</span>
                          </div>
                        </div>
                        
                        {/* Générer les liens de navigation dynamiquement */}
                        {tabConfigs
                          .filter(tab => canAccessTab(tab, user?.role))
                          .map(tab => (
                            <RouterLink
                              key={tab.id}
                              to={`/mon-espace?tab=${tab.id}`}
                              className="flex items-center px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-[#5865F2] transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              {tab.icon}
                              <span className="ml-2">{tab.label}</span>
                            </RouterLink>
                          ))}
                        
                        <button
                          className="flex items-center px-4 py-2 text-sm text-gray-300 hover:text-red-400 hover:bg-red-500/10 transition-colors w-full text-left"
                          onClick={() => {
                            logout();
                            setIsUserMenuOpen(false);
                          }}
                        >
                          <LogOut className="w-5 h-5 mr-2" />
                          <span>Déconnexion</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-white focus:outline-none"
              >
                {isOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`md:hidden ${isOpen ? 'block' : 'hidden'}`}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-[#2F3136]">
            {services.map((service, index) => (
              <div key={index}>
                <button
                  type="button"
                  onClick={() => toggleMobileDropdown(index)}
                  className="flex items-center w-full text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium"
                >
                  {service.icon}
                  <span className="ml-2">{service.name}</span>
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${mobileDropdowns[index] ? 'rotate-180' : ''}`} />
                </button>
                <div className={`pl-6 space-y-1 transition-all duration-200 ${mobileDropdowns[index] ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                  {service.dropdown.map((item, dropdownIndex) => (
                    <RouterLink
                      key={dropdownIndex}
                      to={item.href}
                      className="flex items-center text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.icon}
                      <span className="ml-2">{item.name}</span>
                    </RouterLink>
                  ))}
                </div>
              </div>
            ))}
            <RouterLink
              to="/contact"
              className="bg-[#5865F2] hover:bg-opacity-90 text-white flex items-center px-3 py-2 rounded-md text-base font-medium"
            >
              Nous contacter
            </RouterLink>
            {/* Ajout du lien Mon espace pour mobile */}
            {isAuthenticated && (
              <>
                <RouterLink 
                  to="/mon-espace"
                  className="bg-[#5865F2]/10 text-[#5865F2] flex items-center px-3 py-2 rounded-md text-base font-medium w-full"
                  onClick={() => setIsOpen(false)}
                >
                  <User className="h-5 w-5 mr-2" />
                  <span>Mon profil</span>
                </RouterLink>
                {(user?.role === 'fondateur' || user?.role === 'admin' || user?.role === 'freelancer_admin') && (
                  <>
                    <RouterLink
                      to="/mon-espace?tab=dashboard"
                      className="bg-[#5865F2]/10 text-[#5865F2] flex items-center px-3 py-2 rounded-md text-base font-medium w-full"
                      onClick={() => setIsOpen(false)}
                    >
                      <Grid className="h-5 w-5 mr-2" />
                      <span>Tableau de bord</span>
                    </RouterLink>
                    <RouterLink
                      to="/mon-espace?tab=users"
                      className="bg-[#5865F2]/10 text-[#5865F2] flex items-center px-3 py-2 rounded-md text-base font-medium w-full"
                      onClick={() => setIsOpen(false)}
                    >
                      <Users className="h-5 w-5 mr-2" />
                      <span>Les utilisateurs</span>
                    </RouterLink>
                    <RouterLink
                      to="/mon-espace?tab=freelancers"
                      className="bg-[#5865F2]/10 text-[#5865F2] flex items-center px-3 py-2 rounded-md text-base font-medium w-full"
                      onClick={() => setIsOpen(false)}
                    >
                      <Users className="h-5 w-5 mr-2" />
                      <span>Les freelancers</span>
                    </RouterLink>
                  </>
                )}
                {(user?.role === 'fondateur' || user?.role === 'admin' || user?.role === 'freelancer' || user?.role === 'freelancer_admin') && (
                  <RouterLink
                    to="/mon-espace?tab=ticket_database"
                    className="bg-[#5865F2]/10 text-[#5865F2] flex items-center px-3 py-2 rounded-md text-base font-medium w-full"
                    onClick={() => setIsOpen(false)}
                  >
                    <Database className="h-5 w-5 mr-2" />
                    <span>Base de tickets</span>
                  </RouterLink>
                )}
                {(!user?.role || 
                  (user?.role !== 'fondateur' && 
                   user?.role !== 'freelancer' &&
                   user?.role !== 'freelancer_admin')) && (
                  <>
                    <RouterLink 
                      to="/mon-espace?tab=tickets"
                      className="bg-[#5865F2]/10 text-[#5865F2] flex items-center px-3 py-2 rounded-md text-base font-medium w-full"
                      onClick={() => setIsOpen(false)}
                    >
                      <Ticket className="h-5 w-5 mr-2" />
                      <span>Mes tickets</span>
                    </RouterLink>
                    <RouterLink 
                      to="/mon-espace?tab=subscription"
                      className="bg-[#5865F2]/10 text-[#5865F2] flex items-center px-3 py-2 rounded-md text-base font-medium w-full"
                      onClick={() => setIsOpen(false)}
                    >
                      <CreditCard className="h-5 w-5 mr-2" />
                      <span>Mon abonnement</span>
                    </RouterLink>
                    <RouterLink 
                      to="/mon-espace?tab=invoices"
                      className="bg-[#5865F2]/10 text-[#5865F2] flex items-center px-3 py-2 rounded-md text-base font-medium w-full"
                      onClick={() => setIsOpen(false)}
                    >
                      <FileText className="h-5 w-5 mr-2" />
                      <span>Mes factures</span>
                    </RouterLink>
                  </>
                )}
                <RouterLink 
                  to="/mon-espace?tab=preferences"
                  className="bg-[#5865F2]/10 text-[#5865F2] flex items-center px-3 py-2 rounded-md text-base font-medium w-full"
                  onClick={() => setIsOpen(false)}
                >
                  <Settings className="h-5 w-5 mr-2" />
                  <span>Préférences</span>
                </RouterLink>
                <div className="px-3 py-2 flex flex-col">
                  <span className="font-semibold text-white mb-1 truncate">{user?.firstName} {user?.lastName}</span>
                  <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap overflow-x-auto">
                    {user?.role === 'fondateur' ? (
                      <>
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-red-500/20 text-red-400">
                          Fondateur
                        </span>
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-orange-500/20 text-orange-400">
                          Admin
                        </span>
                      </>
                    ) : user?.role === 'freelancer_admin' ? (
                      <>
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-yellow-500/20 text-yellow-500">
                          Freelancer
                        </span>
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-orange-500/20 text-orange-400">
                          Admin
                        </span>
                      </>
                    ) : (
                      <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${
                        user?.role === 'admin' ? 'bg-orange-500/20 text-orange-400' :
                        user?.role === 'freelancer' ? 'bg-yellow-500/20 text-yellow-500' :
                        'bg-[#5865F2]/20 text-[#5865F2]'
                      }`}>
                        {user?.role === 'admin' ? 'Admin' :
                         user?.role === 'freelancer' ? 'Freelancer' :
                         user?.clientType || 'Utilisateur'}
                      </span>
                    )}
                    {/* Tag d'abonnement pour mobile - visible uniquement pour les utilisateurs standards */}
                    {(!user?.role || 
                      (user?.role !== 'admin' && 
                       user?.role !== 'fondateur' && 
                       user?.role !== 'freelancer' &&
                       user?.role !== 'freelancer_admin')) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-500/20 text-purple-400">
                          <Crown className="w-3 h-3 mr-1" />
                          {getSubscriptionName()}
                        </span>
                      )}
                  </div>
                </div>
              </>
            )}
            <button 
              className={`${isAuthenticated ? 'bg-[#5865F2]' : 'bg-[#4E5058]'} hover:bg-opacity-90 text-white flex items-center px-3 py-2 rounded-md text-base font-medium w-full`}
              aria-label={isAuthenticated ? 'Déconnecter' : 'Se connecter'}
              onClick={isAuthenticated ? logout : () => {
                setIsOpen(false);
                setIsLoginModalOpen(true);
              }}
            >
              {isAuthenticated ? (
                <>
                  <LogOut className="h-5 w-5 mr-2" />
                  <span>Déconnexion</span>
                </>
              ) : (
                <>
                  <User className="h-5 w-5 mr-2" />
                  <span>Connexion</span>
                </>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Login Modal */}
      <Modal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        title="Connexion"
      >
        <LoginForm onSuccess={handleLoginSuccess} onRegisterClick={openRegisterModal} />
      </Modal>
      
      {/* Register Modal */}
      <RegisterModal 
        isOpen={isRegisterModalOpen} 
        onClose={() => setIsRegisterModalOpen(false)} 
        onSwitchToLogin={openLoginModal}
      />
    </>
  );
};

export default Navbar;